import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { financialStatementsApi } from '@/services';

interface EnhancedReportingState {
  cashFlowDirect: any | null;
  comparative: any | null;
  integrityCheck: any | null;
  loading: boolean;
  error: string | null;
  /** In-flight count: the three report thunks are independent. */
  pendingCount: number;
}

const initialState: EnhancedReportingState = {
  cashFlowDirect: null,
  comparative: null,
  integrityCheck: null,
  loading: false,
  error: null,
  pendingCount: 0,
};

/** Guard the envelope: esponse.data.data threw on an empty/malformed body. */
const unwrapData = (response: any): any => response?.data?.data ?? response?.data ?? null;

/**
 * rr.response.data is a raw server envelope (possibly undefined); reduce it
 * to a short, serializable message so state.error always renders.
 */
const toSafeError = (err: any): string => {
  const payload = err?.response?.data;
  const message =
    (typeof payload === 'string' ? payload : payload?.message) ??
    (typeof err?.message === 'string' ? err.message : '');
  return message && message.length <= 300 ? message : 'An unexpected error occurred.';
};

export const fetchCashFlowDirect = createAsyncThunk(
  'enhancedReporting/fetchCashFlowDirect',
  async ({ periodEnd, warehouseId }: { periodEnd: string; warehouseId?: number }, { rejectWithValue }) => {
    try {
      const response = await financialStatementsApi.getCashFlowDirect({ period_end: periodEnd, warehouse_id: warehouseId });
      return unwrapData(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const fetchComparative = createAsyncThunk(
  'enhancedReporting/fetchComparative',
  async ({ type, periodEnd, priorYears = 1, warehouseId }: { type: string; periodEnd: string; priorYears?: number; warehouseId?: number }, { rejectWithValue }) => {
    try {
      const response = await financialStatementsApi.getComparativeStatement(type, { period_end: periodEnd, prior_periods: priorYears, warehouse_id: warehouseId });
      return unwrapData(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const fetchIntegrityCheck = createAsyncThunk(
  'enhancedReporting/fetchIntegrityCheck',
  async (_, { rejectWithValue }) => {
    try {
      const response = await financialStatementsApi.getIntegrityCheck();
      return unwrapData(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

const enhancedReportingSlice = createSlice({
  name: 'enhancedReporting',
  initialState,
  reducers: {
    resetEnhancedReporting(state) {
      state.cashFlowDirect = null;
      state.comparative = null;
      state.integrityCheck = null;
      state.loading = false;
      state.error = null;
      state.pendingCount = 0;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: EnhancedReportingState) => {
      state.pendingCount += 1;
      state.loading = true;
      state.error = null;
    };
    const handleFulfilled = (state: EnhancedReportingState) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
    };
    const handleRejected = (state: EnhancedReportingState, action: any) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
      state.error = typeof action.payload === 'string' ? action.payload : null;
    };

    builder
      .addCase(fetchCashFlowDirect.pending, handlePending)
      .addCase(fetchCashFlowDirect.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.cashFlowDirect = action.payload;
      })
      .addCase(fetchCashFlowDirect.rejected, handleRejected)

      .addCase(fetchComparative.pending, handlePending)
      .addCase(fetchComparative.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.comparative = action.payload;
      })
      .addCase(fetchComparative.rejected, handleRejected)

      .addCase(fetchIntegrityCheck.pending, handlePending)
      .addCase(fetchIntegrityCheck.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.integrityCheck = action.payload;
      })
      .addCase(fetchIntegrityCheck.rejected, handleRejected);
  },
});

export const { resetEnhancedReporting } = enhancedReportingSlice.actions;

export const selectCashFlowDirect = (state: any) => state.enhancedReporting.cashFlowDirect;
export const selectComparative = (state: any) => state.enhancedReporting.comparative;
export const selectIntegrityCheck = (state: any) => state.enhancedReporting.integrityCheck;
export const selectEnhancedReportingLoading = (state: any) => state.enhancedReporting.loading;

export default enhancedReportingSlice.reducer;
